import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@rsos/base-components';
import { useDownloadConversationsMutation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { CommunicationHistoryTableBody } from './CommunicationHistoryTableBody';
import { CommunicationHistoryTableHeader } from './CommunicationHistoryTableHeader';

const CONVERSATION_ID = 'conversation_id';

// Utility function to create a hashed object of IDs from an array
const exportDataID = (arr, key) => {
  const hashedID = {};
  arr.forEach(el => {
    hashedID[el[key]] = true;
  });
  return hashedID;
};

const CommunicationHistoryTable = ({ filteredData }) => {
  const [isSelectedAllChecked, setIsSelectedAllChecked] = useState(false);
  const [checkedList, setCheckedList] = useState({});
  const [displayList, setDisplayList] = useState([]);
  const [isReversed, setIsReversed] = useState(false);
  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      ).name,
  );

  const psapsDisplayName = useSelector(
    state => state.psaps.currentPSAP?.display_name,
  );

  const toggleSelectAll = () => {
    if (isSelectedAllChecked) {
      setCheckedList({});
      setIsSelectedAllChecked(false);
    } else {
      setCheckedList(exportDataID(filteredData, CONVERSATION_ID));
      setIsSelectedAllChecked(true);
    }
  };

  // Handle individual item checkbox toggle
  const handleCheckedData = id => {
    const hasID = checkedList[id];
    setCheckedList(prev => ({
      ...prev,
      [id]: !hasID,
    }));
  };

  const [triggerDownloadConversations] = useDownloadConversationsMutation();

  const onDownloadClicked = useCallback(
    downloadAll => {
      let conversationIds;
      if (downloadAll) {
        conversationIds = filteredData.map(d => d.conversation_id);
      } else {
        conversationIds = Object.keys(checkedList).filter(
          key => checkedList[key],
        );
      }
      triggerDownloadConversations({
        conversationIds,
        orgName,
        psapsDisplayName,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkedList, triggerDownloadConversations, orgName, psapsDisplayName],
  );

  const handleSortingTextHistory = useCallback(
    isReversed => {
      // flips the order of the displayList
      if (isReversed) {
        setDisplayList(filteredData);
      } else {
        setDisplayList([...filteredData].reverse());
      }
      setIsReversed(prev => !prev);
    },
    [filteredData],
  );

  useEffect(() => {
    const newCheckedList = {};
    filteredData.forEach(d => {
      const isChecked = checkedList[d.conversation_id];
      if (isChecked) {
        newCheckedList[d.conversation_id] = true;
      }
    });
    setCheckedList(newCheckedList);
    // keeps the order of the displayList
    if (isReversed) {
      setDisplayList([...filteredData].reverse());
    } else {
      setDisplayList(filteredData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData]);

  useEffect(() => {
    const checkedListValues = Object.values(checkedList);
    const hasUncheckedBox = checkedListValues.some(value => value === false);
    const checkedListLen = checkedListValues.length;

    // Determine if "Select All" should be checked
    const shouldSelectAllBeChecked =
      !hasUncheckedBox &&
      checkedListLen > 0 &&
      checkedListLen === filteredData.length;

    setIsSelectedAllChecked(shouldSelectAllBeChecked);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedList]);

  return (
    <Box overflow="hidden">
      <CommunicationHistoryTableHeader
        toggleSelectAll={toggleSelectAll}
        isSelectedAllChecked={isSelectedAllChecked}
        onDownloadClicked={onDownloadClicked}
        checkedList={checkedList}
        isReversed={isReversed}
        handleSortingTextHistory={handleSortingTextHistory}
      />
      <CommunicationHistoryTableBody
        displayList={displayList}
        isSelectedAllChecked={isSelectedAllChecked}
        checkedList={checkedList}
        setCheckedList={setCheckedList}
        handleCheckedData={handleCheckedData}
      />
    </Box>
  );
};

export default CommunicationHistoryTable;
